import {Injectable} from '@angular/core';
import {StylesService} from '../../styles/styles.service';
import {DiagramJsonHandlerService} from '../../../diagram-data/diagram-json-handler.service';
import {EditorRegistryService} from '../editor/editor-registry.service';
import {
  isActivity,
  isFlowline,
  isLineText,
  isSticker,
  isText
} from '../../helpers/shapes-detectors.util';
import * as _ from 'lodash';
import {DiagramTypeConfigService} from '../../../core/services/diagram-type-config.service';

@Injectable()
export class TextEditingStylesService {
  public diagramJson: any = {};
  public defaultFontSize: string;

  private specialTargetOffsetTop = 0;

  constructor(
    private stylesSvc: StylesService,
    private diagramJsonSvc: DiagramJsonHandlerService,
    private registry: EditorRegistryService,
    private diagramTypeConfigSvc: DiagramTypeConfigService
  ) {
    diagramJsonSvc.diagramJson$.subscribe(json => {
      this.diagramJson = json;
    });
  }

  get fontSizeTag() {
    const tags = this.graph().cellEditor.text2?.getElementsByTagName('*');

    for (let i = 0; i < tags?.length; i++) {
      if (tags[i].style?.fontSize) {
        return tags[i];
      }
    }

    return null;
  }

  getEditorStylings(cell, editor, trigger?) {
    const style: any = {};
    const specialTargetName = trigger?.specialTargetName || '';

    if (trigger?.specialTarget?.offsetTop) {
      this.specialTargetOffsetTop = trigger.specialTarget.offsetTop;
    }

    // style.backgroundImage = TEXT_EDITING.TRANSPARENT_BG;
    style.backgroundColor = 'transparent';

    /**
     * Add padding on top to keep text in the same position as label for activities
     */
    style.paddingTop =
      cell.editingValue && isActivity(cell) ? 32 * this.graph().view.scale + 'px' : '';

    /**
     * Common
     */
    style.cursor = 'text';

    /**
     * Border styling
     */
    style.border = editor.style.border;
    style.outline = editor.style.outline;

    /**
     * Positioning
     */
    style.position = 'absolute';
    _.assign(style, _.pick(editor.style, ['width', 'height', 'left', 'top']));

    /**
     * Font styling
     */
    _.assign(
      style,
      _.pick(editor.style, [
        'color',
        'fontFamily',
        'fontWeight',
        'fontStyle',
        'fontSize',
        'textAlign',
        'textDecoration'
      ])
    );

    const getFontSize = (defaultSize: string) => {
      this.defaultFontSize = defaultSize;

      return (
        parseInt(this.fontSizeTag?.style?.fontSize || defaultSize) * this.graph().view.scale + 'px'
      );
    };

    if (isText(cell)) {
      const textObject = _.find(this.diagramJson.texts, t => +t.mxKey === +cell.id);
      const textSource = this.diagramJson.texts ? textObject : {};
      const textStyle = this.stylesSvc.text(textSource).original();

      style.fontFamily = textStyle.font;
      style.fontWeight = textStyle.fontBold ? 'bold' : 'normal';
      style.fontSize = getFontSize(textStyle.fontSize);
      style.fontStyle = textStyle.fontItalic ? 'italic' : 'none';
      style.color = textStyle.fontColor;
      style.textDecoration = textStyle.fontUnderline ? 'underlined' : 'none';
      style.textAlign = textStyle.textAlign ? textStyle.textAlign : 'center';
      style.height = 'auto';
    }

    if (isFlowline(cell) || isLineText(cell)) {
      const mxStyleObj = this.graph().getView().getState(cell).style;

      style.fontSize = getFontSize(mxStyleObj.fontSize + 'px');
    }

    if (isFlowline(cell) && this.diagramTypeConfigSvc.isArchitecture()) {
      const mxStyleObj = this.graph().getView().getState(cell).style;

      style.outline = 'none';
      style.backgroundColor = 'white';
      style.borderStyle = mxStyleObj.dashed
        ? mxStyleObj.dashPattern
          ? 'dotted'
          : 'dashed'
        : 'solid';
      style.borderColor = mxStyleObj.strokeColor;
      style.borderWidth = mxStyleObj.strokeWidth * this.graph().view.scale + 'px';
      style.marginLeft = -mxStyleObj.strokeWidth * this.graph().view.scale + 'px';
      style.marginTop =
        -(mxStyleObj.strokeWidth + (editor.value ? 5 : 10)) * this.graph().view.scale + 'px';
      style.paddingTop = 5 * this.graph().view.scale + 'px';
      style.paddingLeft = 15 * this.graph().view.scale + 'px';
      style.paddingRight = 15 * this.graph().view.scale + 'px';
      style.paddingBottom = 5 * this.graph().view.scale + 'px';
      style.borderRadius = '50vh';
      style.whiteSpace = 'nowrap';
      style.textAlign = 'center';
      style.width = 'auto';
      style.height = 'auto';
    }

    if (isActivity(cell)) {
      const activity = _.find(this.diagramJson.activities, a => +a.mxKey === +cell.id);
      const activitySource = this.diagramJson.activities ? activity : {};

      const activityStyle = this.stylesSvc.activity(activitySource).original();

      style.color = activityStyle.fontColor;
      style.fontSize = getFontSize(activityStyle.fontSize);
      style.textDecoration = activityStyle.fontUnderline ? 'underline' : 'none';
      style.fontFamily = activityStyle.font;
      style.fontWeight = activityStyle.fontBold ? 'bold' : 'normal';
      style.fontStyle = activityStyle.fontItalic ? 'italic' : 'none';
      style.textAlign = activityStyle.textAlign;
      style.backgroundColor = 'none';
      style.borderColor = 'transparent';
      style.borderWidth =
        parseInt('' + activityStyle.borderWidth, 10) * this.graph().view.scale + 'px';
      style.borderStyle = activityStyle.borderStyle || 'solid';
      style.borderRadius =
        parseInt('' + activityStyle.arcSize, 10) * this.graph().view.scale + 'px';

      style.paddingLeft = 4 * this.graph().view.scale + 'px';
      style.paddingRight = 4 * this.graph().view.scale + 'px';

      // style.paddingTop = (25 * graph().view.scale) + 'px';
      // style.paddingLeft = style.paddingRight = 5 * graph().view.scale + 'px';

      // Border correction
      style.top = parseInt(style.top, 10) + 'px';
      style.width = Math.round(parseInt(style.width, 10)) + 'px';
      style.left = Math.round(parseInt(style.left, 10)) + 'px';

      //@todo: Artem, please....
      const borderCorr = Math.round(
        parseInt('' + activityStyle.borderWidth, 10) * this.graph().view.scale
      );

      if (this.diagramTypeConfigSvc.isArchitecture() && !specialTargetName) {
        const hasIcon = (cell.getJson().resources || []).some(r => !r.hidden && r.main);
        const iconLeftCorr = hasIcon ? 53 : 0;
        const iconTopCorr = hasIcon ? -1 : 0; // I am so sorry for this, but it just won't work correctly without it

        style.paddingTop = 0;
        style.paddingLeft = 0;
        style.paddingRight = 4 * this.graph().view.scale + 'px';
        style.top =
          parseInt(style.top, 10) +
          (20 + iconTopCorr) * this.graph().view.scale +
          borderCorr +
          'px';
        style.paddingBottom = 6 * this.graph().view.scale + 'px';
        style.borderRadius = 0;
        style.borderWidth = 0;
        style.borderBottomColor = '#cccccc';
        style.borderBottomWidth = Math.max(this.graph().view.scale, 1) + 'px';
        style.width =
          Math.round(parseInt(style.width, 10)) -
          (15 + iconLeftCorr) * this.graph().view.scale -
          2 * borderCorr +
          'px';
        style.left =
          Math.round(parseInt(style.left, 10)) +
          (10 + iconLeftCorr) * this.graph().view.scale +
          borderCorr +
          'px';
        style.height = 'auto';
      }

      //@todo: Probably is should not be hardcoded
      if (specialTargetName === 'footerText') {
        style.display = 'flex';
        style.flexWrap = 'wrap';
        style.wordBreak = 'break-word';
        style.alignContent = 'center';
        style.justifyContent = 'center';
        style.textAlign = 'center';
        style.fontSize = getFontSize('12px');
        style.fontWeight = 'normal';
        style.color = '#000000';
        style.backgroundColor = '#e5e5e5';
        style.paddingTop = 0;
        style.paddingLeft = 10 * this.graph().view.scale + 'px';
        style.paddingBottom = 0;
        style.paddingRight = 10 * this.graph().view.scale + 'px';
        style.borderRadius = 0;
        style.borderWidth = 0;
        style.top =
          parseInt(style.top, 10) +
          this.specialTargetOffsetTop * this.graph().view.scale +
          borderCorr +
          'px';
        style.width = Math.round(parseInt(style.width, 10)) - borderCorr * 1.5 + 'px';
        style.height = 'auto';
        style.minHeight = 30 * this.graph().view.scale + 'px';
        style.left = Math.round(parseInt(style.left, 10)) + borderCorr + 'px';
      }

      if (specialTargetName === 'attributeItem') {
        style.display = 'flex';
        style.flexWrap = 'wrap';
        style.wordBreak = 'break-word';
        style.alignContent = 'center';
        style.textAlign = 'left';
        style.fontSize = getFontSize('12px');
        style.fontWeight = 'normal';
        style.color = '#000000';
        style.paddingTop = 0;
        const paddingLeft = this.withPadding(activity) ? 64 : 12;

        style.paddingLeft = paddingLeft * this.graph().view.scale + 'px';
        style.paddingBottom = 0;
        style.paddingRight = 4 * this.graph().view.scale + 'px';
        style.borderRadius = 0;
        style.borderWidth = 0;
        style.top =
          parseInt(style.top, 10) +
          this.specialTargetOffsetTop * this.graph().view.scale +
          borderCorr +
          'px';
        style.width = Math.round(parseInt(style.width, 10)) - borderCorr * 2 + 'px';
        style.height = 'auto';
        style.minHeight = 20 * this.graph().view.scale + 'px';
        style.left = Math.round(parseInt(style.left, 10)) + borderCorr + 'px';
      }
    }

    if (isSticker(cell)) {
      const sticker = _.find(this.diagramJson.notes, n => +n.mxKey === +cell.id);
      const stickerSource = this.diagramJson.notes ? sticker : {};
      const mxStyleObj = this.graph().getView().getState(cell).style;

      style.fontSize = getFontSize(mxStyleObj.fontSize + 'px');
      style.backgroundColor = this.stylesSvc.sticker(stickerSource).paperStyle();
      style.borderColor = '#e3e3e3';
      style.borderWidth = this.graph().view.scale + 'px';
      style.borderStyle = 'solid';
      style.maxWidth = Math.round(parseInt(style.width, 10)) - 10 * this.graph().view.scale + 'px';
      style.maxHeight =
        Math.round(parseInt(style.height, 10)) - 10 * this.graph().view.scale + 'px';
      style.padding = 4 * this.graph().view.scale + 'px';
      style.margin = 5 * this.graph().view.scale + 'px';
      style.fontStyle = 'none';
      style.textAlign = 'center';
      style.transform = 'rotate(' + this.graph().getCellStyle(cell).rotation + 'deg)';
    }

    return style;
  }

  private withPadding(activity) {
    const hasMainIcon = (activity.resources || []).some(r => !r.hidden && r.main);
    const attributePadding = activity.attributePadding ?? false;

    return hasMainIcon && !attributePadding;
  }

  graph() {
    return this.registry.getEditorUI().editor.graph;
  }
}
